// Shared plumbing for report_TankAllocations's LEASE-level mode (added to the
// backend 2026-10-01). This app only ever writes lease-level allocations
// (Allocation.tankId is never set — see allocationSync.ts), so the three
// consumers that need "what share of the oil does this well get" ask for
// allocationType "lease" instead of the old tank-level rows (which came back
// empty for them — the reason leaseAllocationFallback.ts used to exist).
//
// A lease-type row is one per AllocationWell entry (per the backend dev,
// 2026-10-01): wellId + allocation %, with leaseId/leaseName set and NO
// tankNumber. A lease share applies to every tank on the lease ("Applies to
// all N tanks in this lease" on LeaseEditPage), so consumers that work per
// tank fan such a row out with expandLeaseRowsToTanks.

import dayjs from 'dayjs';

export interface AllocationReportArgs {
   allocationType: 'lease';
   /** false for a past as-of date; undefined (omitted -> server default, true) otherwise. */
   isCurrent: false | undefined;
}

/**
 * Query variables for a lease-level report_TankAllocations call. Backend
 * dev: send isCurrent=false for historical queries; there's no reason to send
 * true because an as-of date of today/this month already returns the current
 * entries. A date on/after `today` (including a month-end later in the
 * current month) therefore omits the argument.
 */
export function leaseAllocationReportArgs(
   asOfDate: string,
   today: string = dayjs().format('YYYY-MM-DD'),
): AllocationReportArgs {
   return {allocationType: 'lease', isCurrent: asOfDate < today ? false : undefined};
}

export const hasTankNumber = (tankNumber: string | null | undefined): tankNumber is string =>
   (tankNumber ?? '').trim() !== '';

/**
 * Returns the rows with every tank-less (lease-level) row replaced by one
 * copy per tank on that row's lease. Rows that already name a tank pass
 * through untouched, so this is a no-op for tank-level data.
 * `tankNumbersForWell` returns the active tank numbers on the well's lease.
 */
export function expandLeaseRowsToTanks<R extends {wellId: unknown; tankNumber?: string | null}>(
   rows: readonly R[],
   tankNumbersForWell: (wellId: string) => readonly string[],
): (R & {tankNumber: string})[] {
   const out: (R & {tankNumber: string})[] = [];
   for (const row of rows) {
      if (hasTankNumber(row.tankNumber)) {
         out.push(row as R & {tankNumber: string});
         continue;
      }
      for (const tankNumber of tankNumbersForWell(String(row.wellId))) {
         out.push({...row, tankNumber});
      }
   }
   return out;
}

/**
 * One row per (well, tank): report_TankAllocations can return more than one
 * lease-level row for the same well (e.g. an older allocation period that is
 * still reported as in effect alongside the newer one, or a duplicated
 * AllocationWell entry), and expandLeaseRowsToTanks then fans EACH of them
 * out to every tank — a 1-well / 2-tank lease ended up with four gauge
 * entries (tank A, B, A, B). Keep the row with the latest fromDate (ties:
 * the one with a valid allocation, then the first seen).
 */
export function dedupeAllocationRowsByWellAndTank<
   R extends {wellId: unknown; tankNumber: string; fromDate?: unknown; hasValidAllocation?: unknown},
>(rows: readonly R[]): R[] {
   const best = new Map<string, R>();
   for (const row of rows) {
      const key = `${String(row.wellId).trim().toLowerCase()}:${row.tankNumber.trim().toLowerCase()}`;
      const current = best.get(key);
      if (!current) {
         best.set(key, row);
         continue;
      }
      const a = row.fromDate == null ? '' : String(row.fromDate);
      const b = current.fromDate == null ? '' : String(current.fromDate);
      if (a > b || (a === b && row.hasValidAllocation === true && current.hasValidAllocation !== true)) {
         best.set(key, row);
      }
   }
   return [...best.values()];
}
